import * as ClassNames from 'classnames'
import * as React from 'react'
import styled from 'styled-components'
import I18n from '../../core/i18n'
import * as constants from '../../static/constants'
import { Button } from '../atoms'
import Pagination from '../atoms/Pagination'

export interface IColumn {
  name: string
  field: string
  fullWidth?: boolean
  required?: boolean
  readOnly?: boolean
  link?(record: any): string
  renderElement?(record: any): React.ReactElement
}

interface IProps {
  columns: IColumn[]
  records: any[]
  editable: boolean
  pagination?: any
  loading?: boolean
  isCustomResource?: boolean
  getRowLink?(record: any): string
  handleDelete?(id: number): void
  getEditLink?(record: any): string
  renderRecordControls?(record: any): React.ReactElement
  getResourceByPage?(page: number): void
}

const EditList: React.FC<IProps> = props => {
  const onChangePageHandler = React.useCallback(page => {
    if (props.isCustomResource === true) {
      props.getResourceByPage(page)
    } else {
      location.href = `${location.pathname}?page=${page}`
    }
  }, [])

  const getLink = record => {
    if (typeof props.getRowLink === 'function') {
      return props.getRowLink(record)
    }

    return null
  }

  return (
    <EditListWrapper>
      <div>
        {props.records.map((record, index) => (
          <EditListRow
            key={`${record.id}-${index}`}
            editable={props.editable}
            columns={props.columns}
            record={record}
            link={getLink(record)}
            handleDelete={props.handleDelete}
            getEditLink={props.getEditLink}
            renderRecordControls={props.renderRecordControls}
          />
        ))}
      </div>
      {props.records?.length === 0 && <div className="nodata">{I18n.t('generic.no_data')}</div>}
      {props.pagination && (
        <Pagination
          onChangePageHandler={onChangePageHandler}
          currentPage={props.pagination.current_page}
          prevPage={props.pagination.prev_page}
          nextPage={props.pagination.next_page}
          totalPages={props.pagination.total_pages}
          totalCount={props.pagination.total_count}
        />
      )}
    </EditListWrapper>
  )
}

interface ICategory {
  id: number
  name_i18n: string
  position: number
}

interface IEditListRowProps {
  editable: boolean
  columns: IColumn[]
  record: ICategory
  link?: string
  handleDelete?(id: number): void
  getEditLink(record: any): string
  renderRecordControls?(record: any): React.ReactElement
}

const EditListRow: React.FC<IEditListRowProps> = props => {
  const editLink = typeof props.getEditLink === 'function' ? props.getEditLink(props.record) : null
  const showDetail = () => {
    if (props.link) {
      location.href = props.link
    }
  }

  return (
    <div className={`${props.link ? 'Link ' : ''}List_Row`}>
      {props.columns.map(column => (
        <div
          onClick={showDetail}
          key={column.field}
          className={ClassNames(
            'List_Data',
            {
              hasElement: typeof column.renderElement === 'function',
            },
            {
              fullWidth: column.fullWidth,
            }
          )}
        >
          {typeof column.link === 'function' ? (
            <a href={column.link(props.record)} target="_blank">
              {props.record[column.field]}
            </a>
          ) : typeof column.renderElement === 'function' ? (
            column.renderElement(props.record)
          ) : (
            (typeof props.record[column.field] === 'string' ||
              typeof props.record[column.field] === 'number') &&
            props.record[column.field]
          )}
        </div>
      ))}
      {props.editable && (
        <div className="List_Data _buttons">
          {editLink && (
            <Button key="edit" small={true}>
              <a href={editLink}>{I18n.t('generic.edit')}</a>
            </Button>
          )}
          {props.handleDelete && (
            <Button
              key="delete"
              small={true}
              handleClick={() => props.handleDelete(props.record.id)}
            >
              {I18n.t('generic.delete')}
            </Button>
          )}
          {typeof props.renderRecordControls === 'function' &&
            props.renderRecordControls(props.record)}
        </div>
      )}
    </div>
  )
}

const EditListWrapper = styled.div`
  .Pagination {
    padding-bottom: 24px;
  }

  .EditList_Title {
    font-size: 18px;
    padding: 16px 20px;

    .Button {
      margin-left: 16px;
    }
  }
  .nodata {
    margin: 0 auto 16px;
    padding: 0 24px;
    font-size: 14px;
    font-weight: 700;
  }
  .List {
    min-width: 100%;
    .List_Head {
      border-top: solid 1px ${constants.COLORS.divider};
      @media (max-width: ${constants.BREAKPOINT_TABLET_SMALL}px) {
        display: none;
      }
    }
    .List_Header {
      /* padding: 12px 24px; */
      padding: 0 24px;
      font-size: 12px;
      color: ${constants.COLORS.subText};

      span.indicator {
        cursor: pointer;
      }
    }
    .List_Data {
      /* height: 88px; */
      padding: 0 8px 0 24px;
      @media (max-width: ${constants.BREAKPOINT_TABLET_SMALL}px) {
        padding: 8px 0;
        height: auto;
      }
    }
    .List_Header,
    .List_Data {
      max-width: 400px;
      min-width: 128px;
      font-size: 12px;
      @media (max-width: ${constants.BREAKPOINT_TABLET_SMALL}px) {
        padding: 0;
      }
      &._buttons {
        width: 180px;
        @media (max-width: ${constants.BREAKPOINT_TABLET_SMALL}px) {
          width: auto;
        }
      }
      &.fullWidth {
        max-width: unset;
        min-width: unset;
      }
    }
    > a {
      color: ${constants.COLORS.primary};
    }
  }

  .List_Row {
    border-top: solid 1px ${constants.COLORS.divider};
    display: grid;
    grid-template-columns: repeat(2, auto);
    grid-auto-rows: minmax(0, auto);
    gap: 8px;
    padding: 16px 0;
    @media (max-width: ${constants.BREAKPOINT_TABLET_SMALL}px) {
      grid-template-columns: repeat(1, 1fr);
    }

    &.Link {
      &:hover {
        .List_Data:not(._buttons) {
          cursor: pointer;
          opacity: 0.8;
        }
      }
    }
  }
`

export default EditList
